/**
 * Correlation headers on every API request (RBAC plan §5.2, DEV-3776).
 *
 * The backends log a `request_id` for each request and generate one when the
 * caller sends none; sending our own id from here means a UI action can be
 * traced from a browser network entry to the cloud-core/broker log lines
 * without guessing which generated id was ours. `X-Active-Company-ID` tells
 * cloud-core which company the UI was scoped to when the request was made —
 * it is logging context only, never authorization (cloud-core drops values
 * the token does not allow).
 */
import type { InternalAxiosRequestConfig } from "axios";

export const REQUEST_ID_HEADER = "X-Request-ID";
export const ACTIVE_COMPANY_HEADER = "X-Active-Company-ID";
/**
 * The header and the Faro context read the company from here; the shared provider owns the
 * value and mirrors it into this key, so telemetry keeps the key it has always used.
 */
export const ACTIVE_COMPANY_STORAGE_KEY = "rbac_active_company_id";

/** A 32-hex-char correlation id, matching the format the backends generate. */
export const newRequestId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return Array.from({ length: 32 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join("");
};

const storedActiveCompanyId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Adds the correlation headers to an outgoing request, keeping any value the
 * caller already set.
 */
export const applyObservabilityHeaders = (
  config: InternalAxiosRequestConfig,
  { withActiveCompany = true }: { withActiveCompany?: boolean } = {}
): InternalAxiosRequestConfig => {
  if (!config.headers.has(REQUEST_ID_HEADER)) {
    config.headers.set(REQUEST_ID_HEADER, newRequestId());
  }
  if (withActiveCompany && !config.headers.has(ACTIVE_COMPANY_HEADER)) {
    const activeCompanyId = storedActiveCompanyId();
    if (activeCompanyId) {
      config.headers.set(ACTIVE_COMPANY_HEADER, activeCompanyId);
    }
  }
  return config;
};
